import React, { useEffect, useRef } from 'react';
import Button from './Button';

/**
 * Reusable modal component with gradient border styling.
 * 
 * This component provides the standard GiddyCarrot modal styling:
 * - Gradient border (lime → green → orange)
 * - Escape key to close
 * - Click outside to close (optional)
 * - Responsive sizing
 * - Proper accessibility attributes
 * - Optional built-in action buttons for consistent styling
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Handler for closing the modal
 * @param {string} title - Modal title (optional)
 * @param {string} subtitle - Subtitle text below title (optional)
 * @param {string} maxWidth - Tailwind max-width class (default: 'max-w-2xl')
 * @param {string} titleId - ID for aria-labelledby (default: 'modal-title')
 * @param {boolean} closeOnBackdrop - Whether clicking backdrop closes modal (default: true)
 * @param {React.Ref} contentRef - Ref to attach to the scrollable content area (optional)
 * @param {string} zIndex - Tailwind z-index class (default: 'z-50')
 * @param {boolean} noPadding - When true, removes padding from the scrollable content area (use when content needs to manage its own padding, e.g. for sticky section headers)
 * @param {React.ReactNode} stickyContent - Optional non-scrolling band rendered between the header and the scrollable body (e.g. filter chips, search bar)
 * @param {object} primaryAction - Primary action button config { label, onClick, disabled, loading }
 * @param {object} secondaryAction - Secondary action button config { label, onClick, disabled }
 * @param {React.ReactNode} footerExtra - Optional content rendered above the action buttons in the footer
 * @param {React.ReactNode} children - Modal content
 * 
 * @example
 * // With built-in buttons
 * <GradientModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   title="Edit Item"
 *   subtitle="Make changes below"
 *   primaryAction={{ label: 'Save', onClick: handleSave, loading: saving }}
 *   secondaryAction={{ label: 'Cancel', onClick: handleCancel }}
 * >
 *   <form>...</form>
 * </GradientModal>
 * 
 * @example
 * // Without built-in buttons (include buttons in children)
 * <GradientModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   title="Edit Item"
 * >
 *   <form>...</form>
 *   <ModalButtonGroup onSave={handleSave} onCancel={handleCancel} />
 * </GradientModal>
 */
const GradientModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  maxWidth = 'max-w-2xl',
  titleId = 'modal-title',
  closeOnBackdrop = true,
  contentRef,
  zIndex = 'z-50',
  noPadding = false,
  primaryAction,
  secondaryAction,
  footerExtra,
  stickyContent,
  children
}) => {
  const modalRef = useRef(null);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside modal to close it.
  // Uses e.target === e.currentTarget to ensure we only close when clicking directly
  // on the backdrop element (which has data-modal-backdrop), not when clicks bubble up
  // from inside the modal or from portal-rendered elements like tooltips/dropdowns.
  const handleBackdropClick = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center ${zIndex} p-2 sm:p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      onClick={handleBackdropClick}
      data-modal-backdrop
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-2xl sm:rounded-3xl ${maxWidth} w-full max-h-[95vh] sm:max-h-[90vh] border-4 border-transparent relative overflow-hidden flex flex-col`}
        style={{
          backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #C7F464, #52A675, #F4A261)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box'
        }}
      >
        {/* Title + subtitle (non-scrolling) */}
        {(title || subtitle) && (
          <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-1">
            {title && (
              <h3 id={titleId} className="text-lg sm:text-xl font-bold text-[#2F5233] mb-1">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-[#52A675]">{subtitle}</p>
            )}
          </div>
        )}

        {/* Optional sticky band — always visible, sits between title and scroll body */}
        {stickyContent && (
          <div className="px-4 sm:px-6 py-3 border-b border-gray-100 bg-white">
            {stickyContent}
          </div>
        )}

        {/* Scrollable content area */}
        <div ref={contentRef} className={`${noPadding ? '' : 'p-4 sm:p-6'} overflow-y-auto flex-1`}>
          {children}
        </div>

        {/* Fixed action buttons at bottom */}
        {(primaryAction || secondaryAction || footerExtra) && (
          <div className="flex flex-col items-center gap-2 px-4 sm:px-6 py-4 border-t border-gray-100 bg-white">
            {footerExtra && (
              <div className="w-full text-center">{footerExtra}</div>
            )}
            {(primaryAction || secondaryAction) && (
              <div className="flex justify-center gap-3">
                {primaryAction && (
                  <Button
                    variant="primary"
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled || primaryAction.loading}
                  >
                    {primaryAction.loading ? 'Saving...' : primaryAction.label}
                  </Button>
                )}
                {secondaryAction && (
                  <Button
                    variant="ghost"
                    onClick={secondaryAction.onClick}
                    disabled={secondaryAction.disabled}
                  >
                    {secondaryAction.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GradientModal;
